import React, { useEffect, useState } from 'react';
import { superApi } from './SuperAdminAuth';
import { Building2, Users, CreditCard, Activity } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-3xl font-semibold text-white mt-2">{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-amber-400" />
      </div>
    </div>
  </div>
);

export const SuperDashboardPage = () => {
  const [stats, setStats] = useState({ companies: 0, users: 0, superStaff: 0, audits24h: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [companiesR, staffR, auditR] = await Promise.all([
          superApi.get('/super/companies').catch(() => ({ data: { companies: [] } })),
          superApi.get('/super/staff'),
          superApi.get('/super/audit-log?limit=500'),
        ]);
        const companies = companiesR.data.companies || [];
        const audits = auditR.data.entries || [];
        const last24h = audits.filter((a) => {
          const t = new Date(a.timestamp);
          return (Date.now() - t.getTime()) < 24 * 60 * 60 * 1000;
        }).length;
        setStats({
          companies: companies.length,
          users: companies.reduce((acc, c) => acc + (c.user_count || 0), 0),
          superStaff: (staffR.data.staff || []).length,
          audits24h: last24h,
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-white">Platform Overview</h1>
        <p className="text-sm text-slate-400 mt-1">High-level health of the Opswarex platform.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Building2} label="Companies" value={stats.companies} hint="Active tenants" />
        <StatCard icon={Users} label="Total Users" value={stats.users} hint="Across all tenants" />
        <StatCard icon={Activity} label="Super Staff" value={stats.superStaff} hint="Platform operators" />
        <StatCard icon={CreditCard} label="Audit actions (24h)" value={stats.audits24h} />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="text-sm font-medium text-white mb-2">Quick actions</div>
        <div className="text-xs text-slate-400">Use the sidebar to navigate to Companies, Billing, Staff, or Platform Email.</div>
      </div>
    </div>
  );
};

export default SuperDashboardPage;
